import { Button } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Plot, { createTimeHoverProps } from "../components/ThreediPlot";
import DownloadCsvIcon from "../icons/download_csv_icon.svg?react";
import { PumpDischargeChart } from "../state/selectedAsset";
import { getSimulation } from "../state/simulation";
import { Asset } from "../types/assets";
import { forceValidOrNull } from "../utils/isValidData";

import { PropertyTableHeader } from "./PropertyTableHeader";
import { exportToCSV, getFilename } from "./TimeseriesChart";

interface Props {
  asset: Asset;
  dichargeGraph: PumpDischargeChart;
}

const TimeseriesDischargeChart: React.FC<Props> = (props) => {
  const dischargeGraph = props.dichargeGraph;
  const simulationName = useSelector(getSimulation).name;
  const dischargeArray = forceValidOrNull((dischargeGraph || []).map((element) => element.value));
  const timestamps = (dischargeGraph || []).map((element) => element.timestamp);
  const timeOfDischarge = (dischargeGraph || []).map((element) => {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    originalDate.setSeconds(originalDate.getSeconds() + element.timestamp);
    return originalDate.toISOString();
  });
  const lastTimeDischargeTimeseriesGraph = (function () {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    if (dischargeGraph.length && dischargeGraph[dischargeGraph.length - 1]) {
      const lastValue = dischargeGraph[dischargeGraph.length - 1];
      originalDate.setSeconds(originalDate.getSeconds() + lastValue.timestamp + 900);
    }
    return originalDate.toISOString();
  })();

  const intl = useIntl();

  return (
    <div>
      <PropertyTableHeader
        button={
          <Button
            variant="secondary"
            leftIcon={<DownloadCsvIcon />}
            onClick={() =>
              exportToCSV(
                "Discharge",
                timestamps,
                dischargeArray,
                getFilename(simulationName, props.asset.properties.site_id, "Discharge")
              )
            }
          >
            CSV
          </Button>
        }
      >
        {" "}
        <FormattedMessage
          id="timeseriesWaterFlowChart.chartTitleDischarge"
          defaultMessage="Discharge (m³/s)"
        />
      </PropertyTableHeader>
      <Plot
        style={{
          position: "relative",
          display: "inline-block",
          border: "1px solid #707070",
          borderRadius: 5,
          overflow: "hidden",
        }}
        data={[
          {
            ...createTimeHoverProps(timeOfDischarge),
            y: dischargeArray,
            type: "scatter",
            mode: "lines+markers",
            marker: { color: "#699fbe" },
          },
        ]}
        layout={{
          showlegend: false,
          width: 335,
          height: 335,
          paper_bgcolor: "#f2f2ec",
          plot_bgcolor: "#f2f2ec",
          margin: {
            l: 40,
            r: 10,
            b: 10,
            t: 10,
          },
          xaxis: {
            tickformat:
              "%H:%M\n" +
              intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisLabel",
                defaultMessage: "day",
              }) +
              "%_d",
            // solution from // https://github.com/plotly/plotly.js/issues/1464 that didnot work
            // tickformat: "bla###%H:%M",
            title: {
              text: intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisTime",
                defaultMessage: "Time (hours : mins) &#8594;",
              }),
              standoff: 10,
            },
            showticklabels: timeOfDischarge.length > 0,
            zeroline: false,
            automargin: true,
            range: [timeOfDischarge[0], lastTimeDischargeTimeseriesGraph],
          },
          yaxis: {
            title: {
              text: "(m³/s) &#8594;",
              standoff: 5,
            },
            exponentformat: "power",
            showline: true,
            zeroline: false,
            automargin: true,
          },
        }}
      />
    </div>
  );
};

export default TimeseriesDischargeChart;
